import React, { useEffect, useState, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

const Modal = ({ isOpen, title, children, onClose, wide = false }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className={`bg-white rounded-lg p-6 w-full my-8 ${wide ? 'max-w-4xl' : 'max-w-2xl'}`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const MaintenanceForm = ({ initialValues, onSubmit, onCancel, cabins, equipment }) => {
  const [values, setValues] = useState(() => ({
    resource_type: 'cabin',
    resource_id: '',
    resource_name: '',
    start_date: '',
    end_date: '',
    start_time: '09:00',
    end_time: '17:00',
    recurrence_type: 'once',
    recurrence_days: '',
    reason: '',
    notes: '',
    status: 'scheduled',
    quantity: 1,
    ...initialValues
  }));

  const handleChange = (name, value) => {
    setValues(v => {
      const newValues = { ...v, [name]: value };
      
      // Auto-populate resource_name when resource_id changes
      if (name === 'resource_id') {
        const resourceList = newValues.resource_type === 'cabin' ? cabins : equipment;
        const selected = resourceList.find(r => r.id === parseInt(value));
        if (selected) {
          newValues.resource_name = selected.name;
        }
      }
      
      // Auto-set end_date when start_date changes (default to same day)
      if (name === 'start_date' && !v.end_date) {
        newValues.end_date = value;
      }
      
      return newValues;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!values.resource_id) {
      alert('Please select a cabin or equipment');
      return;
    }
    if (!values.start_date || !values.end_date) {
      alert('Please provide start and end dates');
      return;
    }
    if (new Date(values.end_date) < new Date(values.start_date)) {
      alert('End date must be on or after start date');
      return;
    }
    
    onSubmit(values);
  };

  const resourceList = values.resource_type === 'cabin' ? cabins : equipment;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Resource Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Resource Type *
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
            value={values.resource_type}
            onChange={(e) => {
              handleChange('resource_type', e.target.value);
              handleChange('resource_id', '');
              handleChange('resource_name', '');
            }}
            required
          >
            <option value="cabin">Cabin</option>
            <option value="equipment">Equipment</option>
          </select>
        </div>

        {/* Resource Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {values.resource_type === 'cabin' ? 'Cabin' : 'Equipment'} *
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
            value={values.resource_id}
            onChange={(e) => handleChange('resource_id', e.target.value)}
            required
          >
            <option value="">Select...</option>
            {resourceList.map(r => (
              <option key={r.id} value={r.id}>
                {r.name} {r.cabin_no ? `(${r.cabin_no})` : ''}
                {r.total_quantity ? ` - Qty: ${r.total_quantity}` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date *
          </label>
          <input
            type="date"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
            value={values.start_date}
            onChange={(e) => handleChange('start_date', e.target.value)}
            required
          />
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date *
          </label>
          <input
            type="date"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
            value={values.end_date}
            onChange={(e) => handleChange('end_date', e.target.value)}
            required
          />
        </div>

        {/* Start Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Time *
          </label>
          <input
            type="time"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
            value={values.start_time}
            onChange={(e) => handleChange('start_time', e.target.value)}
            required
          />
        </div>

        {/* End Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Time *
          </label>
          <input
            type="time"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
            value={values.end_time}
            onChange={(e) => handleChange('end_time', e.target.value)}
            required
          />
        </div>

        {/* Recurrence Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recurrence
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
            value={values.recurrence_type}
            onChange={(e) => handleChange('recurrence_type', e.target.value)}
          >
            <option value="once">Once</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>

        {/* Recurrence Days (for weekly) */}
        {values.recurrence_type === 'weekly' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Days (e.g., 0,2,4 for Mon, Wed, Fri)
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
              value={values.recurrence_days}
              onChange={(e) => handleChange('recurrence_days', e.target.value)}
              placeholder="0,1,2,3,4,5,6"
            />
          </div>
        )}

        {/* Quantity (for equipment only) */}
        {values.resource_type === 'equipment' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity Under Maintenance *
            </label>
            <input
              type="number"
              min="1"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
              value={values.quantity}
              onChange={(e) => handleChange('quantity', parseInt(e.target.value))}
              required
            />
          </div>
        )}

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
            value={values.status}
            onChange={(e) => handleChange('status', e.target.value)}
          >
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Reason */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason
          </label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984]"
            value={values.reason}
            onChange={(e) => handleChange('reason', e.target.value)}
            placeholder="e.g., Regular maintenance, Repair work"
          />
        </div>

        {/* Notes */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#E4C984] resize-y"
            value={values.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={2}
            placeholder="Additional notes..."
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-3 border-t">
        <button 
          type="button" 
          onClick={onCancel} 
          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium"
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="px-4 py-2 rounded-md hover:opacity-90 text-sm font-medium"
          style={{ backgroundColor: '#E4C984', color: '#171717' }}
        >
          Save
        </button>
      </div>
    </form>
  );
};

const Maintenance = () => {
  const [records, setRecords] = useState([]);
  const [cabins, setCabins] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [filterResourceType, setFilterResourceType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchRecords();
    fetchLookups();
  }, [refreshTick]);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const res = await apiGet('/api/maintenance');
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load maintenance records');
      setRecords(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLookups = async () => {
    try {
      const [cabinsRes, equipRes] = await Promise.all([
        apiGet('/api/admin/rooms'),
        apiGet('/api/admin/equipment'),
      ]);
      const [cabinsData, equipData] = await Promise.all([
        cabinsRes.json(),
        equipRes.json(),
      ]);
      setCabins(cabinsData || []);
      setEquipment(equipData || []);
    } catch (e) {
      console.error('Failed to load lookups:', e);
    }
  };

  const filteredRecords = useMemo(() => {
    const q = (search || '').toLowerCase();
    return records.filter(r => {
      const matchesSearch = !q || 
        r.resource_name?.toLowerCase().includes(q) ||
        r.reason?.toLowerCase().includes(q) ||
        r.notes?.toLowerCase().includes(q);
      const matchesType = !filterResourceType || r.resource_type === filterResourceType;
      const matchesStatus = !filterStatus || r.status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [records, search, filterResourceType, filterStatus]);

  const openCreate = () => {
    setEditingRecord(null);
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleSave = async (formValues) => {
    try {
      setLoading(true);
      setError(null);
      const path = editingRecord ? `/api/maintenance/${editingRecord.id}` : '/api/maintenance';
      const res = editingRecord 
        ? await apiPut(path, formValues)
        : await apiPost(path, formValues);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      setRefreshTick(t => t + 1);
      closeModal();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete maintenance record for "${record.resource_name}"?`)) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiDelete(`/api/maintenance/${record.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      setRefreshTick(t => t + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5); // HH:MM
  };

  const getStatusBadge = (status) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="p-4" style={{ backgroundColor: '#f6f6f6' }}>
      <h3 className="text-lg font-semibold mb-3" style={{ color: '#171717' }}>Maintenance Management</h3>
      
      {/* Controls */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex gap-2">
          <input
            className="border rounded-md px-3 py-2 text-sm w-64"
            placeholder="Search by name, reason, or notes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={filterResourceType}
            onChange={e => setFilterResourceType(e.target.value)}
          >
            <option value="">All Types</option>
            <option value="cabin">Cabin</option>
            <option value="equipment">Equipment</option>
          </select>
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <button
          onClick={openCreate}
          className="px-3 py-2 text-sm rounded-md inline-flex items-center gap-2"
          style={{ backgroundColor: '#E4C984', color: '#171717' }}
        >
          <AddOutlinedIcon fontSize="small" />
          Add Maintenance
        </button>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-50 text-red-700 rounded">{error}</div>
      )}

      {/* Table */}
      <div className="overflow-auto border rounded-md bg-white" style={{ borderColor: '#e5e7eb', borderRadius: '16px', boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Resource</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Date Range</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Time</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Recurrence</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Reason</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Qty</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="px-3 py-4" colSpan={9}>Loading...</td></tr>
            ) : filteredRecords.length === 0 ? (
              <tr><td className="px-3 py-4" colSpan={9}>No maintenance records found</td></tr>
            ) : filteredRecords.map(record => (
              <tr key={record.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2">
                  {record.resource_type === 'cabin' ? (
                    <span className="inline-flex items-center gap-1">
                      <MeetingRoomOutlinedIcon fontSize="small" style={{ color: '#E4C984' }} />
                      Cabin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <BuildOutlinedIcon fontSize="small" style={{ color: '#E4C984' }} />
                      Equipment
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-semibold text-gray-900">{record.resource_name}</td>
                <td className="px-3 py-2">
                  {formatDate(record.start_date)} - {formatDate(record.end_date)}
                </td>
                <td className="px-3 py-2">
                  {formatTime(record.start_time)} - {formatTime(record.end_time)}
                </td>
                <td className="px-3 py-2 capitalize">{record.recurrence_type}</td>
                <td className="px-3 py-2">{record.reason || '-'}</td>
                <td className="px-3 py-2">{getStatusBadge(record.status)}</td>
                <td className="px-3 py-2">{record.resource_type === 'equipment' ? record.quantity : '-'}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button 
                    onClick={() => openEdit(record)} 
                    className="p-1 hover:bg-gray-100 rounded" 
                    style={{ color: '#cbb16f' }}
                    title="Edit"
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </button>
                  <button 
                    onClick={() => handleDelete(record)} 
                    className="ml-1 p-1 hover:bg-red-50 rounded text-red-600"
                    title="Delete"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal 
        isOpen={modalOpen} 
        title={`${editingRecord ? 'Edit' : 'Add'} Maintenance Record`} 
        onClose={closeModal}
        wide={true}
      >
        <MaintenanceForm
          initialValues={editingRecord || {}}
          onSubmit={handleSave}
          onCancel={closeModal}
          cabins={cabins}
          equipment={equipment}
        />
      </Modal>
    </div>
  );
};

export default Maintenance;

